// 文件校验逻辑
const VALID_FILE_TYPES = ['application/pdf']
const MAX_FILE_SIZE = 100 * 1024 * 1024 // 100MB

export function validateFile(file) {
  if (!VALID_FILE_TYPES.includes(file.type)) {
    throw new Error('[validateFile]仅支持PDF文件')
  }
  if (file.size > MAX_FILE_SIZE) {
    throw new Error('[validateFile]文件大小超过100MB限制')
  }
}

/**
 * @description 下载流
 * @export
 */
export function downloadBlob(data, config = { options: { type: '' }, name: '下载文件' }) {
  const blobOptions = { ...config.options }
  if (!blobOptions.type) {
    data.type && (blobOptions.type = data.type)
  }
  const blob = new Blob([data], config.options)
  const link = document.createElement('a')
  const url = window.URL.createObjectURL(blob)
  link.href = url
  link.download = config.name
  document.body.appendChild(link)
  link.click()
  window.URL.revokeObjectURL(url)
  document.body.removeChild(link)
}

// #region task-runner
/**
 * 异步执行任务队列，根据环境支持自动选择 requestIdleCallback 或 setTimeout 调度
 * @param {Array<Function>} tasks - 要执行的任务队列，每个任务为返回Promise的异步函数
 * @param {Function} [callback] - 所有任务完成后的回调函数
 * @param {AbortController.signal} signal - 用于中止任务执行的中断控制器
 * @returns {void}
 */
export let runTaskQueue = function (tasks = [], callback, signal) {
  // 根据浏览器特性动态重写函数实现
  const hasIdleCallback = typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function'

  if (hasIdleCallback) {
    // 使用 requestIdleCallback 的高性能实现
    runTaskQueue = (tasks = [], callback, signal) => {
      let idleCallbackId = null

      /** 清除空闲回调的通用方法 */
      function clearIdleCallback() {
        if (idleCallbackId) {
          window.cancelIdleCallback(idleCallbackId)
          idleCallbackId = null
        }
      }

      /** 核心任务执行逻辑，处理空闲时段的任务调度 */
      function runTask(deadline) {
        return new Promise((resolve, reject) => {
          // 处理任务中止逻辑
          if (signal && signal.aborted) {
            clearIdleCallback()
            reject('任务中止')
            return
          }

          const execute = async () => {
            try {
              // 在空闲时段执行单个任务
              if (deadline.timeRemaining() > 0 && tasks.length > 0) {
                const task = tasks.shift()
                await task()
              }

              // 递归调度后续任务或触发完成回调
              if (tasks.length > 0) {
                idleCallbackId = window.requestIdleCallback(runTask)
              } else {
                idleCallbackId = null
                typeof callback === 'function' && callback()
              }
              resolve()
            } catch (error) {
              reject(error)
            }
          }
          execute()
        })
      }

      // 注册中断事件监听
      if (signal && !signal.aborted) {
        signal.addEventListener('abort', clearIdleCallback, { once: true })
      }

      // 启动任务队列调度
      idleCallbackId = window.requestIdleCallback(runTask)
    }
  } else {
    // 使用 setTimeout 的降级实现
    runTaskQueue = (tasks = [], callback, signal) => {
      let timer = null

      /** 清除定时器的通用方法 */
      function clearTimer() {
        if (timer) {
          clearTimeout(timer)
          timer = null
        }
      }

      /** 核心任务执行逻辑，使用事件循环调度 */
      function runTask() {
        return new Promise((resolve, reject) => {
          // 处理任务中止逻辑
          if (signal && signal.aborted) {
            clearTimer()
            reject('任务中止')
            return
          }

          const execute = async () => {
            try {
              // 执行单个任务
              if (tasks.length > 0) {
                const task = tasks.shift()
                await task()
              }

              // 递归调度后续任务或触发完成回调
              if (tasks.length > 0) {
                timer = setTimeout(runTask, 0)
              } else {
                timer = null
                typeof callback === 'function' && callback()
              }
              resolve()
            } catch (error) {
              reject(error)
            }
          }
          execute()
        })
      }

      // 注册中断事件监听
      if (signal && !signal.aborted) {
        signal.addEventListener('abort', clearTimer, { once: true })
      }

      // 启动任务队列调度
      timer = setTimeout(runTask, 0)
    }
  }

  // 执行初始化调用
  runTaskQueue(tasks, callback, signal)
}
// #endregion

export function checkAbortSignal(signal, message = '任务中止') {
  if (signal && signal.aborted) throw new Error(message)
}

